import { ValidSystemType } from 'data/types';
import { getSwapSlot, SwapLocks } from 'utils/system-swap';

// A zone eligible for swapping: only zones that currently have a selection.
export type SwapZone = {
  uniqueId: string;
  selectedProductId: number | null;
};

export type SwapOptionIds = {
  condenserProductIds: number[];
  airHandlerProductIdsByZone: Record<string, number[]>;
};

const getSlotProductId = (
  system: ValidSystemType,
  zoneNumber: number
): number | null | undefined =>
  system[`airHandler${zoneNumber}ProductId` as keyof ValidSystemType] as
    | number
    | null
    | undefined;

const systemHoldsLockedZones = (
  system: ValidSystemType,
  zones: SwapZone[],
  lockedZoneIds: string[],
  excludeZoneId?: string
): boolean =>
  lockedZoneIds
    .filter((zoneId) => zoneId !== excludeZoneId)
    .every((zoneId) => {
      // A row without a slot for this zone would read as undefined, which
      // must never pass a lock (undefined === undefined) — fail explicitly.
      const zoneNumber = getSwapSlot(system, 'airHandler', zoneId);
      if (zoneNumber === -1) return false;
      const lockedProductId = zones.find(
        (zone) => zone.uniqueId === zoneId
      )?.selectedProductId;
      return getSlotProductId(system, zoneNumber) === lockedProductId;
    });

/**
 * Derives the swap popover option lists from validSystems rows.
 *
 * - Condenser list: condensers from rows that keep every locked zone's
 *   current air handler.
 * - Per-zone AH list: air handlers appearing in that zone's slot in rows
 *   that keep the current condenser (when locked) and every OTHER locked
 *   zone's air handler.
 * - No locks: full lists (legacy behavior; cross-manufacturer options are
 *   intentional — selecting one triggers the announced fallback swap).
 *
 * Slots are addressed per row via the zone's uniqueId (airHandlerNUnique),
 * never by the zone's position in the local zones array, since rows can
 * order zones differently.
 */
export function deriveSwapOptionIds(
  validSystems: ValidSystemType[],
  zones: SwapZone[],
  currentCondenserProductId: number | undefined,
  locks: SwapLocks
): SwapOptionIds {
  const condenserProductIds = validSystems
    .filter((system) =>
      systemHoldsLockedZones(system, zones, locks.lockedZoneIds)
    )
    .map((system) => system.condenserProductId)
    .filter((id) => id !== currentCondenserProductId);

  const airHandlerProductIdsByZone: Record<string, number[]> = {};
  zones.forEach((zone) => {
    airHandlerProductIdsByZone[zone.uniqueId] = validSystems
      .filter(
        (system) =>
          !locks.condenserLocked ||
          system.condenserProductId === currentCondenserProductId
      )
      .filter((system) =>
        systemHoldsLockedZones(
          system,
          zones,
          locks.lockedZoneIds,
          zone.uniqueId
        )
      )
      .map((system) => {
        const zoneNumber = getSwapSlot(system, 'airHandler', zone.uniqueId);
        return zoneNumber === -1 ? null : getSlotProductId(system, zoneNumber);
      })
      .filter(
        (id): id is number => id != null && id !== zone.selectedProductId
      );
  });

  return { condenserProductIds, airHandlerProductIdsByZone };
}
